const CDN = 'https://clan.steamstatic.com/images/45150627';

export const NEWS = [
  {
    slug: 'dev-note-0-1-2',
    img: `${CDN}/36615cd6c8e4acfc6e12b6b4baa901046d204208.png`,
    heroImg: `${CDN}/aba4139c7a8f3fa371808b892f231408eed1e50d.png`,
    date: 'October 19, 2025',
    title: 'New Task System, Five-Tier Ranks & Dynamic Weather — Blackfield Update 0.1.2',
    excerpt: 'Blackfield\'s latest update brings a full quest and task management system, a five-tier rank progression, dynamic weather that affects gameplay, and a wave of performance improvements — here\'s everything that changed.',
    body: [
      {
        heading: 'Quest & Task Management System',
        items: [
          'Players can now track objectives in real time through the ARK_OS task log — tasks auto-detect based on player actions in the world.',
          'Quests trigger based on location: entering a district or picking up an object can instantly unlock or complete a task.',
          'Complex multi-condition tasks are now supported — a single objective can require picking up an item, reaching a location, and triggering a scene event.',
          'Task chains link objectives in sequence, so completing one mission naturally leads into the next part of the story.',
          'A visual task editor allows rapid content creation and auto-syncs changes at runtime without restarting the game.',
        ],
      },
      {
        heading: 'HUD & World Interface',
        items: [
          'The in-game HUD now shows live weather conditions, current time, and district name — all as part of the world\'s ARK_OS aesthetic.',
          'An interactive map highlights task locations and points of interest, making navigation more intuitive.',
          'The diary system now organises quests and orders with visibility toggles — players decide what appears on screen.',
          'Collected items appear as sprites in the inventory panel, giving visual confirmation of what Marta is carrying.',
        ],
      },
      {
        heading: 'Rank & Survival Systems',
        items: [
          'Marta can now progress through five ranks — Trainee, Junior, Senior, Expert, and Master — each unlocking new capabilities.',
          'Survival stats including hunger, hydration, and stamina are actively monitored and reflected back to the player through in-world systems.',
          'A step counter and distance tracker add a quiet layer of realism to exploration across Blackfield\'s desolate environments.',
        ],
      },
      {
        heading: 'Gameplay Mechanics',
        items: [
          'The cooking system now supports food assembly machines, NPC meal requests, and ingredient management — feeding allies has real consequences.',
          'A centralised device controller manages all player input across keyboard, controller, and future mobile layouts.',
          'Environmental conditions update dynamically and affect how NPCs behave and which tasks become available.',
        ],
      },
      {
        heading: 'Performance & Stability',
        items: [
          'Memory usage has been significantly reduced through optimised asset loading and runtime monitoring.',
          'An auto-save system ensures task progress is never lost, even during crashes or unexpected exits.',
          'Cross-scene references now resolve at runtime, eliminating a category of hard-to-diagnose bugs in complex level transitions.',
        ],
      },
      {
        heading: 'Visual Polish & Effects',
        items: [
          'A new camera obstruction fader smoothly hides geometry that blocks the player\'s view, keeping the action readable.',
          'Vehicles now emit light and smoke effects that react to speed and terrain.',
          'Objective markers and NPC indicators appear as diegetic in-world elements, preserving Blackfield\'s atmospheric visual language.',
        ],
      },
    ],
  },
  {
    slug: 'dev-note-0-1-1',
    img: `${CDN}/ea3cf74a359e923f5440930810529da62d9cdedd.png`,
    heroImg: `${CDN}/86f676a07a39dd1bcf14bf6a877fc48f1ce74c81.png`,
    date: 'August 6, 2025',
    title: 'Kiosk Trading, Real Fog Effects & Weight Mechanics — Blackfield Update 0.1.1',
    excerpt: 'This update introduces a full kiosk trading system where players buy gear and ingredients, smooth fog transitions tied to the rain cycle, a jetpack vehicle, and a weight system that makes every item Marta carries matter.',
    body: [
      {
        heading: 'Kiosk Trading System',
        items: [
          'Players can now browse and purchase gear and ingredients through in-world kiosks — each stall displays items visually with sprites loaded automatically from game data.',
          'Kiosks switch between gear mode and ingredient mode depending on location and story context.',
          'The trading interface was built for speed: items appear with previews, prices, and availability at a glance.',
        ],
      },
      {
        heading: 'Weather & Visual Systems',
        items: [
          'Rain now triggers a gradual fog effect that builds over time and fades when the weather clears — environments feel alive and unpredictable.',
          'All in-world text and ingredient labels become semi-transparent during heavy fog, reinforcing the sense of reduced visibility.',
          'The camera dynamically shifts distance and applies a subtle shake when Marta breaks into a sprint or lands from a jump.',
          'The backpack visually evolves through four stages as Marta fills her inventory — light and empty versus heavy and packed look noticeably different.',
        ],
      },
      {
        heading: 'Vehicles & Equipment',
        items: [
          'A jetpack has been added with its own mesh, controller, and visual rig — early traversal for reaching otherwise inaccessible parts of the world.',
          'Scene assets have been reorganised from the prototype city layout into a more structured environment for better performance.',
          'Gear data is now driven by a centralised JSON structure, making it straightforward to add new equipment as development continues.',
        ],
      },
      {
        heading: 'Inventory & Weight Mechanics',
        items: [
          'Carrying weight now directly affects movement — the heavier Marta\'s load, the slower she runs, pushing players to make real choices about what to carry.',
          'Items spawn in the world using a probability curve system, meaning rare resources feel genuinely scarce.',
          'New consumable food items have been added to the world, giving players more options for managing hunger and stamina.',
        ],
      },
      {
        heading: 'Bug Fixes',
        items: [
          'Fixed a missing gear data file that caused inventory errors on scene load.',
          'Resolved broken prefab references and duplicate collision meshes in the city environment.',
          'Corrected item preparation timing and synchronisation logic in the cooking system.',
          'Fixed a weather system integration issue that caused fog to appear during clear weather.',
        ],
      },
    ],
  },
  {
    slug: 'dev-note-0-1-0',
    img: `${CDN}/4a57bde9af4d43336bd2f8ffdd37ec3b2ad1aa8b.png`,
    heroImg: `${CDN}/474af2116150ff416661f9e24584ef54aef28176.png`,
    date: 'July 4, 2025',
    title: 'Hovercraft Physics, Cooking System & Full Inventory Rebuild — Blackfield 0.1.0',
    excerpt: 'The first major Blackfield milestone: a fully reworked hovercraft with particle effects and real air physics, an expanded cooking system with unique meals and dual-tab UI, a revamped diary tracking stamina and quests, and a complete inventory rebuild.',
    body: [
      {
        heading: 'Hovercraft & Vehicle Physics',
        items: [
          'The hovercraft now emits particle smoke and uses spotlight effects that react to the environment — traversal feels cinematic.',
          'Air physics have been rebuilt from the ground up with tilt logic and float mechanics, making the vehicle feel genuinely suspended above the surface.',
          'Vehicle movement parameters were fine-tuned across a full pass to remove jitter and improve responsiveness at speed.',
        ],
      },
      {
        heading: 'Cooking & Food System',
        items: [
          'Every meal in Blackfield now has a unique prefab and ingredient sprite — food is visual, tactile, and meaningful to survival.',
          'The cooking interface uses a dual-tab layout separating meal selection from ingredient management, reducing cognitive load during tense moments.',
          'Ingredient text and food timers update in real time so players always know exactly how long a meal takes and what is inside it.',
          'Randomised food spawning means no two playthroughs begin with the same resources in the same locations.',
        ],
      },
      {
        heading: 'Diary & Quest Tracking',
        items: [
          'The diary system has been fully revamped — it now tracks main quests, side orders, stamina, hunger, and hydration in a single integrated log.',
          'Visual checkmarks mark completed side quests; the log can be toggled on and off without interrupting gameplay.',
          'Quest sprites and main objective icons have been updated to match Blackfield\'s overall visual identity.',
        ],
      },
      {
        heading: 'Inventory & Weight System',
        items: [
          'A complete inventory rebuild introduces item grabbing, carrying logic, and a backpack weight system — every object Marta picks up has a cost.',
          'Food can now spawn directly inside the log list area, blending survival management with quest tracking.',
          'Empty inventory slots and dish displays now have improved visuals, making the current state of resources immediately readable.',
        ],
      },
      {
        heading: 'Bug Fixes & Quality of Life',
        items: [
          'Fixed air vehicle physics that caused unnatural bouncing on uneven terrain.',
          'Resolved a bug where player movement triggered during cooking device interactions.',
          'The ESC key now immediately closes any active GUI menu, giving players a fast exit from any screen.',
          'Save game state management has been improved — progress is written more frequently and verified on load.',
          'Camera angle inconsistencies and object naming issues have been cleaned up across all prototype scenes.',
        ],
      },
    ],
  },
];
